import React from 'react';
import { 
  ConsentLoadingState, 
  ConsentFormLayout, 
  ConsentCompletionState,
  ConsentUnauthorizedState
} from './shared/ConsentPageLayout';
import { useConsentManagement } from '../hooks/useConsentManagement';
import { LoadingState } from '../config/consentConfig';

/**
 * GuestPage Component - HRx Investigation Consent Service
 * 
 * This component provides the public access interface for the HRx Investigation
 * Consent Service. It uses the new shared components and custom hook for consistent
 * behavior and reduced code duplication.
 * 
 * Key Features:
 * - Public access without authentication requirements
 * - Text-based consent validation requiring exact phrase entry
 * - Same comprehensive consent information as authenticated flow
 * - Real-time validation and error handling
 * - Confirmation screen with submission details
 * - Enhanced error handling and retry mechanisms
 * 
 * Security Features:
 * - Requires typing exact consent phrase: "I Consent"
 * - Prevents accidental submissions through validation
 * - Maintains audit trail for all consent decisions
 * - Secure data handling for non-employee participants
 * 
 * This component ensures that external participants (contractors, vendors,
 * witnesses, etc.) can provide legally valid consent for investigation
 * recording and data storage.
 */

export const GuestPage: React.FC = () => {
  // Use the consent management hook
  const {
    state,
    isSubmitDisabled,
    isAcceptEnabled,
    requiredConsentText,
    handleConsentDecisionChange,
    handleConsentTextChange,
    handleSubmit
  } = useConsentManagement({
    userType: 'guest',
    requireTextInput: true // Guest page needs text input validation
  });

  // Show loading state while validating token
  if (state.initializationState === LoadingState.LOADING) {
    return (
      <ConsentLoadingState
        title="Validating Access..."
        message="Please wait while we validate your access token."
      />
    );
  }

  // Show unauthorized access error if token validation failed
  if (state.initializationState === LoadingState.ERROR) {
    return (
      <ConsentUnauthorizedState
        message={state.validationError || 'Invalid or expired access token.'}
      />
    );
  }

  // Show success/completion screen
  if (state.hasConsented) {
    return (
      <ConsentCompletionState
        consentDecision={state.consentDecision!}
        tokenData={state.tokenData || undefined}
        userType="guest"
      />
    );
  }

  // Main consent form
  return (
    <ConsentFormLayout
      consentDecision={state.consentDecision}
      onConsentDecisionChange={handleConsentDecisionChange}
      showTextInput={true}
      consentText={state.consentText}
      onConsentTextChange={handleConsentTextChange}
      validationError={state.validationError}
      requiredConsentText={requiredConsentText}
      isAcceptEnabled={isAcceptEnabled}
      onSubmit={handleSubmit}
      isSubmitDisabled={isSubmitDisabled}
      isLoading={state.consentSubmissionState === LoadingState.LOADING}
    />
  );
};

export default GuestPage;
